/**
 * The one question a programmer must answer before a show with artists is saved:
 * does this artist get emailed their link, or are you sending it yourself?
 *
 * It exists because the answer used to be implied by two separate controls — an email
 * strategy and a "generate artist-facing link" checkbox — with a silent dependency between
 * them. A show could look fully configured and quietly email nobody, which is exactly the
 * failure this prevents. Choosing "email" here mints the link as a side effect, so there is
 * no second step to forget.
 *
 * Asked once per show, and never again once an invite has gone out, so routine re-saves
 * stay quiet.
 */

import { Link2, Mail, Send } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

export interface SendInviteDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Who the combined invite would go to. */
    artistName: string;
    /** How many others would get a profile-only email alongside it. */
    otherArtistCount: number;
    /** True when this show has no artist-facing link yet, so choosing to email creates one. */
    willCreateLink: boolean;
    onSendEmail: () => void;
    onSendManually: () => void;
    isLoading?: boolean;
}

export function SendInviteDialog({
    open,
    onOpenChange,
    artistName,
    otherArtistCount,
    willCreateLink,
    onSendEmail,
    onSendManually,
    isLoading = false,
}: SendInviteDialogProps) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent size="medium">
                <DialogHeader>
                    <DialogTitle>Send {artistName} their invite?</DialogTitle>
                    <DialogDescription>
                        Saving now. Should we email the invite, or are you sending the link yourself?
                    </DialogDescription>
                </DialogHeader>

                <div className="mx-4 space-y-3 sm:mx-6">
                    <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm">
                        <p className="flex items-center gap-2 font-medium">
                            <Mail className="h-4 w-4 text-primary" />
                            One email, one link
                        </p>
                        <p className="mt-1 text-muted-foreground">
                            {artistName} completes their profile and fills in the show details in the
                            same place.
                            {otherArtistCount > 0 && (
                                <>
                                    {' '}
                                    {otherArtistCount} other artist{otherArtistCount > 1 ? 's' : ''} get
                                    {otherArtistCount > 1 ? '' : 's'} a profile-only email.
                                </>
                            )}
                        </p>
                        {willCreateLink && (
                            <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                                <Link2 className="h-3.5 w-3.5" />
                                This creates the artist-facing link for the show.
                            </p>
                        )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                        Prefer WhatsApp or a DM? Choose "I'll send it myself" — the link is still
                        created, and you can copy it from this dialog at any time.
                    </p>
                </div>

                <DialogFooter>
                    <Button type="button" variant="outline" onClick={onSendManually} disabled={isLoading}>
                        I'll send it myself
                    </Button>
                    <Button type="button" onClick={onSendEmail} disabled={isLoading}>
                        <Send className="mr-2 h-4 w-4" />
                        Email {artistName}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
